import { useEffect, useState } from 'react'
import { deletePlaylist, fetchPlaylists, savePlaylist, type Playlist, type PlaylistEntry } from '../data/api'
import { useCanCoach } from '../data/session'

// Named, ordered sets of rounds — "their A executes", "our bad ecos".
//
// Deliberately not the same thing as a saved situation. A situation stores a query and re-runs it,
// so it stays current and can surprise you; a playlist stores exactly what somebody picked and
// keeps it that way, which is what you want when the list IS the session rather than a search you
// repeat. Order is the curation, so nothing here sorts.

export function Playlists({
  candidates,
  onOpen,
}: {
  /** Rounds currently on screen in the finder, offered as things to add. */
  candidates: PlaylistEntry[]
  onOpen: (demoId: string, round: number) => void
}) {
  const canCoach = useCanCoach()
  const [lists, setLists] = useState<Playlist[]>([])
  const [name, setName] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = () => fetchPlaylists().then(setLists).catch(() => setLists([]))
  useEffect(() => {
    load()
  }, [])

  const create = async () => {
    const n = name.trim()
    if (!n) return
    try {
      // Created from what's on screen: a playlist you have to fill one round at a time is one
      // nobody fills. The finder already narrowed it down — that IS the picking.
      await savePlaylist({ name: n, entries: candidates.slice(0, 60) })
      setName('')
      setErr(null)
      load()
    } catch (e) {
      setErr((e as Error).message === 'too_many' ? 'That is a lot of playlists — delete one first.' : (e as Error).message)
    }
  }

  const open = lists.find((p) => p.id === openId) ?? null

  return (
    <div className="mt-4 border-t border-border pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-text-faint">Playlists</span>
        {lists.length === 0 && <span className="text-[11px] text-text-faint">none yet</span>}
        {lists.map((p) => (
          <button
            key={p.id}
            onClick={() => setOpenId(openId === p.id ? null : p.id)}
            aria-pressed={openId === p.id}
            className="border border-border px-2 py-1 text-[11px] text-text-dim hover:border-border-bright hover:text-accent"
            style={{ borderRadius: 'var(--radius)' }}
          >
            {p.name} <span className="tabular-nums opacity-60">{p.entries.length}</span>
          </button>
        ))}
      </div>

      {canCoach && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="save these rounds as…"
            maxLength={60}
            aria-label="Name for this playlist"
            className="min-w-56 flex-1 border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
            style={{ borderRadius: 'var(--radius)' }}
          />
          <button
            onClick={create}
            disabled={!name.trim() || !candidates.length}
            className="border border-border-bright px-3 py-1 text-xs text-accent disabled:opacity-40"
            style={{ borderRadius: 'var(--radius)' }}
          >
            save {candidates.length} round{candidates.length === 1 ? '' : 's'}
          </button>
        </div>
      )}

      {err && (
        <p role="alert" className="mt-2 text-xs text-loss">
          {err}
        </p>
      )}

      {open && (
        <div className="mt-3">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-sm text-text">{open.name}</span>
            <span className="text-[11px] text-text-faint">by {open.createdByName}</span>
            {canCoach && (
              <button
                onClick={async () => {
                  if (!confirm(`Delete the playlist “${open.name}”?`)) return
                  await deletePlaylist(open.id).catch(() => {})
                  setOpenId(null)
                  load()
                }}
                className="ml-auto text-[11px] text-text-faint hover:text-loss"
              >
                delete
              </button>
            )}
          </div>
          {open.entries.length === 0 ? (
            <p className="mt-2 text-xs text-text-faint">
              Empty — every match it pointed at has been deleted. The list is kept because you named
              it; removing it is your call.
            </p>
          ) : (
            <ol className="mt-2 space-y-1">
              {open.entries.map((e, i) => (
                <li key={`${e.demoId}-${e.round}`} className="flex items-baseline gap-2 text-xs">
                  <span className="tabular-nums text-text-faint">{i + 1}.</span>
                  <button onClick={() => onOpen(e.demoId, e.round)} className="text-accent hover:underline">
                    round {e.round}
                  </button>
                  <span className="text-text-faint">{e.why}</span>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-2 text-[11px] text-text-faint">
            In the order they were saved — a playlist that sorts itself is a query with extra steps.
          </p>
        </div>
      )}
    </div>
  )
}
